<template>
  <div>
    <div class="page-header">
      <div class="page-icon">📦</div>
      <h2>库存列表</h2>
    </div>

    <!-- 搜索框 -->
    <el-input
      v-model="searchQuery"
      placeholder="请输入商品名称搜索"
      style="width: 200px; margin-bottom: 20px;"
      clearable
    />
    
    <!-- 包裹表格的卡片 -->
    <el-card class="box-card">
      <!-- 库存表格 -->
      <el-table :data="paginatedStock" stripe>
        <el-table-column prop="product_name" label="商品名称"></el-table-column>
        <el-table-column label="平均成本">
          <template #default="{ row }">
            <span class="cost-wrapper">
              <!-- 确保 average_cost 是数字，并处理 null/undefined -->
              {{ formatAverageCost(row.average_cost) }}
              <sup
                v-if="row.delta !== undefined && row.delta !== 0"
                :style="{ color: row.delta >= 0 ?  '#F56C6C':'#67C23A' , fontSize: '12px', marginLeft: '2px' }"
              >
                {{ row.delta >= 0 ? '+' : '' }}{{ row.delta.toFixed(1) }}
              </sup>
            </span>
          </template>
        </el-table-column>
        <el-table-column label="库存数量">
          <template #default="{ row }">
            <!-- 点击时显示输入框，失去焦点或按回车保存 -->
            <div v-if="editingStockId === row.product_id" class="edit-input-wrapper">
              <el-input
                v-model="editingQuantity"
                type="number"
                size="small"
                @blur="saveStockQuantity(row)"
                @keyup.enter="saveStockQuantity(row)"
                ref="quantityInput"
                style="width: 100px;"
              />
            </div>
            <!-- 默认显示库存数量，点击时进入编辑模式 -->
            <span
              v-else
              class="editable-quantity"
              @click="startEditing(row)"
            >
              {{ row.stock_quantity }}
            </span>
          </template>
        </el-table-column>
        <el-table-column label="库存价值" :formatter="formatStockValue"></el-table-column>
        <el-table-column label="操作" width="120" align="center">
          <template #default="{ row }">
            <el-button size="small" type="primary" plain @click="openHistoryDialog(row)">📊 成本历史</el-button>
          </template>
        </el-table-column>
      </el-table>
    </el-card>

    <!-- 总价值显示 -->
    <div style="margin-top: 20px; font-size: 16px;">
      <strong>所有商品总价值：{{ totalStockValue }}</strong>
    </div>
    <div style="margin-top: 20px; font-size: 16px;">
      <strong>所有商品总条数：{{ totalStockQuantity }}</strong>
    </div>

    <!-- 分页 -->
    <el-pagination
      :page-size="pageSize"
      :current-page="currentPage"
      :total="filteredStock.length"
      @current-change="handlePageChange"
      layout="total, prev, pager, next, jumper"
      style="margin-top: 20px; text-align: center;"
    />

    <!-- 成本历史对话框 -->
    <el-dialog
      v-model="historyDialogVisible"
      :title="`${currentProductName} 成本变化历史`"
      width="900px"
      @close="closeHistoryDialog"
    >
      <div v-if="historyLoading" style="text-align: center; padding: 40px;">
        <el-icon class="is-loading"><Loading /></el-icon>
        <span style="margin-left: 10px;">加载中...</span>
      </div>
      <div v-else-if="historyData.length === 0" style="text-align: center; padding: 40px; color: #909399;">
        该商品暂无成本变化记录
      </div>
      <el-table v-else :data="historyData" stripe max-height="500">
        <el-table-column label="变化时间" min-width="160">
          <template #default="{ row }">{{ formatDateTime(row.change_date) }}</template>
        </el-table-column>
        <el-table-column label="变化前" width="120" align="right">
          <template #default="{ row }">¥ {{ row.previous_average_cost.toFixed(2) }}</template>
        </el-table-column>
        <el-table-column label="变化后" width="120" align="right">
          <template #default="{ row }">¥ {{ row.new_average_cost.toFixed(2) }}</template>
        </el-table-column>
        <el-table-column label="差额" width="100" align="center">
          <template #default="{ row }">
            <span :style="{ color: row.delta >= 0 ? '#F56C6C' : '#67C23A', fontWeight: 600 }">
              {{ row.delta >= 0 ? '+' : '' }}{{ row.delta.toFixed(2) }}
            </span>
          </template>
        </el-table-column>
        <el-table-column label="关联交易 ID" prop="transaction_id" width="120" align="center" />
      </el-table>
      <template #footer>
        <el-button @click="closeHistoryDialog">关闭</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, nextTick } from 'vue';
import axios from 'axios';
import API_BASE from '@/api';
import { ElMessage } from 'element-plus';
import { Loading } from '@element-plus/icons-vue';

const stock = ref([]);
const searchQuery = ref('');
const currentPage = ref(1);
const pageSize = ref(17);

// 编辑状态
const editingStockId = ref(null); // 当前编辑的 product_id
const editingQuantity = ref(null); // 当前编辑的库存数量

// 成本历史对话框状态
const historyDialogVisible = ref(false);
const historyLoading = ref(false);
const historyData = ref([]);
const currentProductName = ref('');

// 打开成本历史对话框
const openHistoryDialog = async (row) => {
  currentProductName.value = row.product_name;
  historyDialogVisible.value = true;
  historyLoading.value = true;
  historyData.value = [];
  try {
    const res = await axios.get(`${API_BASE}/api/stock-history/${row.product_id}`);
    if (res.data.success) {
      historyData.value = res.data.history;
    } else {
      ElMessage.error('加载成本历史失败：' + (res.data.message || ''));
    }
  } catch (error) {
    ElMessage.error('请求出错：' + error.message);
  } finally {
    historyLoading.value = false;
  }
};

// 关闭成本历史对话框
const closeHistoryDialog = () => {
  historyDialogVisible.value = false;
  historyData.value = [];
};

// 格式化日期时间（YYYY-MM-DD HH:mm:ss）
const formatDateTime = (value) => {
  if (!value) return '';
  const d = new Date(value);
  if (isNaN(d.getTime())) return value;
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
};

// 获取库存数据
const getStock = async () => {
  try {
    const response = await axios.get(`${API_BASE}/api/stock`);
    console.log(response);
    if (response.data.success) {
      stock.value = response.data.stock;
    } else {
      ElMessage.error('获取库存失败：' + response.data.message);
    }
  } catch (error) {
    ElMessage.error('请求出错：' + error.message);
  }
};

// 格式化 average_cost，确保是数字并保留两位小数
const formatAverageCost = (value) => {
  const num = Number(value);
  return isNaN(num) ? '0.00' : num.toFixed(2);
};

// 格式化库存总价值
const formatStockValue = (row) => {
  const averageCost = Number(row.average_cost);
  const stockQuantity = Number(row.stock_quantity);
  if (isNaN(averageCost) || isNaN(stockQuantity)) {
    return '0.00';
  }
  return (averageCost * stockQuantity).toFixed(2);
};

// 根据搜索条件过滤库存
const filteredStock = computed(() => {
  return stock.value.filter(item => {
    return item.product_name.toLowerCase().includes(searchQuery.value.toLowerCase());
  });
});

// 计算所有商品的总价值
const totalStockValue = computed(() => {
  return filteredStock.value.reduce((total, item) => {
    const averageCost = Number(item.average_cost);
    const stockQuantity = Number(item.stock_quantity);
    if (isNaN(averageCost) || isNaN(stockQuantity)) {
      return total;
    }
    return total + (averageCost * stockQuantity);
  }, 0).toFixed(2);
});

// 计算所有商品的总库存数量
const totalStockQuantity = computed(() => {
  return filteredStock.value.reduce((total, item) => {
    return total + Number(item.stock_quantity || 0);
  }, 0);
});

// 分页后的库存数据
const paginatedStock = computed(() => {
  const start = (currentPage.value - 1) * pageSize.value;
  const end = start + pageSize.value;
  return filteredStock.value.slice(start, end);
});

// 分页处理
const handlePageChange = (page) => {
  currentPage.value = page;
};

// 进入编辑模式
const startEditing = (row) => {
  editingStockId.value = row.product_id;
  editingQuantity.value = row.stock_quantity;
  // 等待 DOM 更新后聚焦输入框
  nextTick(() => {
    const input = document.querySelector('.edit-input-wrapper .el-input__inner');
    if (input) input.focus();
  });
};

// 保存库存数量
const saveStockQuantity = async (row) => {
  const newQuantity = Number(editingQuantity.value);
  if (isNaN(newQuantity) || newQuantity < 0) {
    ElMessage.error('请输入有效的库存数量（大于等于 0）');
    return;
  }

  try {
    const response = await axios.put(
      `${API_BASE}/api/stock/update-quantity`,
      {
        product_id: row.product_id,
        stock_quantity: newQuantity,
      }
    );

    if (response.data.success) {
      // 更新前端数据
      row.stock_quantity = newQuantity;
      ElMessage.success('库存数量更新成功');
    } else {
      ElMessage.error('更新失败：' + response.data.message);
    }
  } catch (error) {
    ElMessage.error('请求出错：' + error.message);
  } finally {
    // 退出编辑模式
    editingStockId.value = null;
    editingQuantity.value = null;
  }
};

// 页面加载时获取库存数据
onMounted(() => {
  getStock();
});
</script>

<style scoped>
.box-card {
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-card);
  padding: 20px;
  margin-top: 8px;
  border: 1px solid var(--border-lighter);
}

.el-table {
  border: none;
}

.el-table .el-table__row {
  border: none;
}

.el-table-column {
  padding: 0;
}

.cost-wrapper {
  position: relative;
}

sup {
  vertical-align: super;
  font-size: 12px;
  margin-left: 2px;
}

.editable-quantity {
  cursor: default;
  padding: 5px;
  display: inline-block;
}

.editable-quantity:hover {
  background-color: var(--bg-hover);
  border-radius: 4px;
}

.edit-input-wrapper {
  display: inline-block;
}
</style>
